const openerByDialog = new WeakMap();
const setupMarker = Symbol.for("rcd.dialog.setup");
const focusableSelector = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])"
].join(",");

const isNativeDialog = (element) => element?.tagName === "DIALOG";

const resolveDialog = (target) => {
    if (target instanceof HTMLElement) {
        return isNativeDialog(target) || target.matches("[data-rcd-dialog]") ? target : null;
    }

    if (typeof target !== "string") {
        return null;
    }

    const id = target.startsWith("#") ? target.slice(1) : target;
    const byId = document.getElementById(id);
    if (byId instanceof HTMLElement) {
        return resolveDialog(byId);
    }

    try {
        return resolveDialog(document.querySelector(target));
    } catch {
        return null;
    }
};

const isDialogOpen = (dialog) => isNativeDialog(dialog)
    ? dialog.open
    : dialog.dataset.rcdDialogState === "open" && !dialog.hidden;

const dialogSurface = (dialog) => {
    if (isNativeDialog(dialog) || dialog.matches("[role='dialog'], [role='alertdialog']")) {
        return dialog;
    }

    return dialog.querySelector("[role='dialog'], [role='alertdialog'], .rcd-dialog__surface") || dialog;
};

const syncDocumentState = () => {
    const hasOpenDialog = [...document.querySelectorAll("dialog[data-rcd-dialog], [data-rcd-dialog]:not(dialog)")]
        .some(isDialogOpen);
    document.documentElement.toggleAttribute("data-rcd-dialog-open", hasOpenDialog);
};

const focusableControls = (dialog) => [...dialogSurface(dialog).querySelectorAll(focusableSelector)]
    .filter((element) => !element.hidden && element.getAttribute("aria-hidden") !== "true");

const focusInitialControl = (dialog) => {
    const surface = dialogSurface(dialog);
    const initialControl = dialog.querySelector("[data-rcd-dialog-initial-focus]")
        || focusableControls(dialog)[0]
        || surface;

    if (!(initialControl instanceof HTMLElement)) {
        return;
    }

    if (initialControl === surface && !surface.hasAttribute("tabindex")) {
        surface.setAttribute("tabindex", "-1");
    }

    window.requestAnimationFrame(() => {
        if (isDialogOpen(dialog) && initialControl.isConnected) {
            initialControl.focus({ preventScroll: true });
        }
    });
};

const restoreOpenerFocus = (dialog) => {
    if (dialog.dataset.rcdDialogReturnFocus === "false") {
        openerByDialog.delete(dialog);
        return;
    }

    const opener = openerByDialog.get(dialog);
    openerByDialog.delete(dialog);
    if (!(opener instanceof HTMLElement) || !opener.isConnected) {
        return;
    }

    window.requestAnimationFrame(() => opener.isConnected && opener.focus({ preventScroll: true }));
};

const dispatchClosed = (dialog) => {
    restoreOpenerFocus(dialog);
    syncDocumentState();
    dialog.dispatchEvent(new CustomEvent("rcd:dialog:close", {
        bubbles: true,
        detail: { returnValue: dialog.returnValue || "" }
    }));
};

export const openRcdDialog = (target, opener = document.activeElement) => {
    const dialog = resolveDialog(target);
    if (!dialog?.isConnected) {
        return false;
    }

    if (isDialogOpen(dialog)) {
        return true;
    }

    if (opener instanceof HTMLElement) {
        openerByDialog.set(dialog, opener);
    }

    dialog.returnValue = "";
    if (isNativeDialog(dialog)) {
        if (typeof dialog.showModal !== "function") {
            return false;
        }
        dialog.showModal();
    } else {
        dialog.hidden = false;
        dialog.removeAttribute("aria-hidden");
        dialog.dataset.rcdDialogState = "open";
        const surface = dialogSurface(dialog);
        surface.setAttribute("aria-modal", "true");
        if (!surface.hasAttribute("role")) {
            surface.setAttribute("role", "dialog");
        }
    }

    syncDocumentState();
    focusInitialControl(dialog);
    dialog.dispatchEvent(new CustomEvent("rcd:dialog:open", { bubbles: true }));
    return true;
};

export const closeRcdDialog = (target, returnValue = "") => {
    const dialog = resolveDialog(target);
    if (!dialog || !isDialogOpen(dialog)) {
        return false;
    }

    dialog.returnValue = String(returnValue);
    if (isNativeDialog(dialog)) {
        dialog.close(dialog.returnValue);
    } else {
        dialog.hidden = true;
        dialog.setAttribute("aria-hidden", "true");
        delete dialog.dataset.rcdDialogState;
        dispatchClosed(dialog);
    }

    return true;
};

const isBackdropClick = (event, dialog) => {
    if (event.target !== dialog) {
        return false;
    }

    if (!isNativeDialog(dialog)) {
        return true;
    }

    const bounds = dialog.getBoundingClientRect();
    return event.clientX < bounds.left || event.clientX > bounds.right
        || event.clientY < bounds.top || event.clientY > bounds.bottom;
};

const topmostCustomDialog = () => [...document.querySelectorAll("[data-rcd-dialog]:not(dialog)")]
    .filter(isDialogOpen)
    .at(-1);

const trapFocus = (event, dialog) => {
    const controls = focusableControls(dialog);
    if (controls.length === 0) {
        event.preventDefault();
        dialogSurface(dialog).focus();
        return;
    }

    const first = controls[0];
    const last = controls.at(-1);
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
};

export const setupRcdDialogs = () => {
    if (window[setupMarker]) {
        return window.rcdDialog;
    }

    window[setupMarker] = true;

    document.addEventListener("click", (event) => {
        if (!(event.target instanceof Element)) {
            return;
        }

        const closeTrigger = event.target.closest("[data-rcd-dialog-close]");
        if (closeTrigger) {
            const dialog = closeTrigger.closest("[data-rcd-dialog]");
            if (dialog) {
                event.preventDefault();
                closeRcdDialog(dialog, closeTrigger.dataset.rcdDialogReturnValue || "");
            }
            return;
        }

        const openTrigger = event.target.closest("[data-rcd-dialog-open]");
        if (openTrigger) {
            const opened = openRcdDialog(openTrigger.dataset.rcdDialogOpen, openTrigger);
            if (opened) {
                event.preventDefault();
            }
            return;
        }

        const dialog = event.target.closest("[data-rcd-dialog]");
        if (dialog && dialog.dataset.rcdDialogDismissible !== "false" && isBackdropClick(event, dialog)) {
            closeRcdDialog(dialog, "dismiss");
        }
    });

    document.addEventListener("cancel", (event) => {
        const dialog = event.target;
        if (dialog instanceof HTMLElement && isNativeDialog(dialog) && dialog.dataset.rcdDialogDismissible === "false") {
            event.preventDefault();
        }
    }, true);

    document.addEventListener("keydown", (event) => {
        const dialog = topmostCustomDialog();
        if (!dialog) {
            return;
        }
        if (event.key === "Escape" && dialog.dataset.rcdDialogDismissible !== "false") {
            event.preventDefault();
            closeRcdDialog(dialog, "dismiss");
        } else if (event.key === "Tab") {
            trapFocus(event, dialog);
        }
    });

    document.addEventListener("htmx:beforeSwap", (event) => {
        const swapTarget = event.detail?.target;
        if (!(swapTarget instanceof Element)) {
            return;
        }

        const candidates = swapTarget.matches("[data-rcd-dialog]")
            ? [swapTarget]
            : [...swapTarget.querySelectorAll("[data-rcd-dialog]")];
        candidates.filter(isDialogOpen).forEach((dialog) => closeRcdDialog(dialog, "swap"));
    });

    document.addEventListener("close", (event) => {
        const dialog = event.target;
        if (!(dialog instanceof HTMLElement) || !isNativeDialog(dialog) || !dialog.matches("[data-rcd-dialog]")) {
            return;
        }
        dispatchClosed(dialog);
    }, true);

    syncDocumentState();
    window.rcdDialog = { open: openRcdDialog, close: closeRcdDialog };
    return window.rcdDialog;
};
